export type Mutation = {
  __typename?: 'Mutation';
  register: string;
  resetPassword: boolean;
  setNewPassword: string;
  changePassword: boolean;
  updateCompany: CompanyInput;
  createCustomFormula: CustomFormula;
  deleteCustomFormula: boolean;
  updateCustomFormula: CustomFormula;
  createCustomReport: CustomReport;
  addFacebookConnector: string;
  addGoogleAdsConnector: string;
  addGoogleAnalyticsConnector: string;
  addGoogleSearchConsoleConnector: string;
  addShopifyConnector: string;
  deleteConnector: boolean;
  selectAnalyticsAccounts: boolean;
  selectGoogleAdsAccounts: boolean;
  selectFacebookAccounts: boolean;
  selectGoogleSearchConsoleSites: boolean;
  startConversation: Conversation;
  continueConversation: ConversationMessage;
  setConversationMessageReaction: boolean;
  updateConversationContext: boolean;
};

export type Query = {
  __typename?: 'Query';
  signIn: string;
  company: CompanyInput;
  getAllCustomFormulas: CustomFormula[];
  getCustomFormulaValues: MetricValuesRow[];
  getCustomFormulaById: CustomFormula;
  getFacebookMetrics: MetricGroup;
  getGoogleAnalyticsMetrics: MetricGroup;
  getGoogleSearchConsoleMetrics: MetricGroup;
  getGoogleSearchConsoleMetricValues: MetricValuesRow[];
  getGoogleAnalytics4Metrics: MetricValuesRow[];
  getGoogleAdsMetricsList: MetricGroup;
  getShopifyMetrics: MetricGroup;
  getMagentoMetrics: MetricGroup;
  getGoogleAdsMetrics: any;
  getUnifiedSearchTerms: UnifiedSearchTerm[];

  me: User;
  getFacebookAuthUrl: string;
  getGoogleAdsAuthUrl: string;
  getAnalyticsAuthUrl: string;
  getGoogleSearchConsoleAuthUrl: string;
  getShopifyAuthUrl: string;
  getConnectors: Connector[];
  getConnectorSelections: ConnectorSelection[];
  getAvailableFiltersForMetric: Filter[];
  getFilters: Filter[];
  getAnalyticsAccounts: AnalyticsAccount[];
  getGoogleAdsAccounts: GoogleAdsAccount[];
  getFacebookAdAccounts: FacebookAdAccount[];
  getGoogleSearchConsoleSites: SearchConsoleSite[];
  getDatabaseConnections: DatabaseConnection[];
  getShopifyMetricValues: ShopifyMetricValues;
  getMagentoMetricValues: ShopifyMetricValues;
  getFacebookMetricsValues: MetricValuesRow[];
  getKipifyMetricsValues: MetricValuesRow[];
  getShopifyFilters: ShopifyFilter[];
  getMagentoFilters: ShopifyFilter[];
  getSplits: Split[];
  checkConnectorTokenValidity: boolean;
  getFilterAvailableValues: string[];
};

export type ShopifyFilter = {
  availableOperators: { label: string; key: string }[];
  key: string;
  expectedReturnValueType: FilterValueType;
};

export type MutationStartConversationArgs = {
  userInput: string;
  currentDate: string;
};

export type MutationContinueConversationArgs = MutationStartConversationArgs & {
  conversationId: string;
};

export type QuerySignInArgs = {
  signInData: SignInInput;
};

export type SignInInput = {
  email: string;
  password: string;
};

export type QueryGetCustomFormulaByIdArgs = {
  id: string;
};

export type QueryGetAvailableFiltersForMetricArgs = {
  metricId: string;
};

export type QueryGetAvailableFiltersArgs = {
  connectorType: ConnectorType;
};

export type QueryGetShopifyAuthUrlArgs = {
  shop: string;
};

export type QueryGetConnectorsArgs = {
  type?: ConnectorType;
};

export type QueryGetSplitsArgs = {
  connectorType?: ConnectorType;
};

export type MutationRegisterArgs = {
  registerData: RegisterUserInput;
};

export type MutationResetPasswordArgs = {
  email: string;
};

export type MutationSetNewPasswordArgs = {
  secret: string;
  newPassword: string;
};

export type MutationChangePasswordArgs = {
  input: ChangePasswordInput;
};

export type MutationUpdateCompanyArgs = {
  newCompanyData: CompanyInput;
};

export type MutationCreateCustomFormulaArgs = {
  customFormulaData: CustomFormulaInput;
};

export type MutationUpdateCustomFormulaArgs = {
  customFormulaData: UpdateCustomFormulaInput;
};

export type MutationDeleteCustomFormulaArgs = {
  id: string;
};

export type MutationDeleteConnectorArgs = {
  connectorId: string;
};

export type MutationAddFacebookConnectorArgs = {
  code: string;
};

export type MutationAddGoogleAdsConnectorArgs =
  MutationAddFacebookConnectorArgs;
export type MutationAddGoogleAnalyticsConnectorArgs =
  MutationAddFacebookConnectorArgs;
export type MutationAddGoogleSearchConsoleConnectorArgs =
  MutationAddFacebookConnectorArgs;
export type MutationAddShopifyConnectorArgs = MutationAddFacebookConnectorArgs;

export type MutationUpdateConversationContextArgs = {
  conversationId: string;
  dateRange: DateRangeInput;
  metricValuesArgsGroupedByConnector: MetricValuesArgsGroupedByConnector;
  metricValuesRowsGroupedByConnector: {
    [key in ConnectorType]?: MetricValuesRow[];
  } & {
    customFormula?: MetricValuesRow[];
  };
};

export type RegisterUserInput = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyData: CompanyInput;
};

export type CompanyInput = {
  usageOptions: UsageOption[];
  name: string;
  industry: string;
  companyDescription: string;
};

export type CustomFormula = {
  id: string;
  name: string;
  formulaData: CustomFormulaItem[];
  format: FormulaOutputFormatInput;
};

export type CustomFormulaInput = {
  name: string;
  customFormulaItems: CustomFormulaItem[];
  format: FormulaOutputFormatInput;
};

export type FormulaOutputFormatInput = {
  type: MetricFormatType;
  description: string;
  prefix?: string;
  suffix?: string;
  isDecimal: boolean;
};

export type UpdateCustomFormulaInput = CustomFormulaInput & {
  id: string;
};

export type CustomFormulaItem = {
  label: string;
  value: string;
  type: CustomFormulaItemType;
  metricId?: string;
  connectorType?: ConnectorType | MetricsByConnectorType;
  filters?: GA4FilterItem[];
};

export type GetGoogleAdsMetricsVariables = {
  args: MetricValuesArgs;
  clientAccountId: string;
};

export type GetFacebookMetricValuesVariables = {
  args: MetricValuesArgs;
};

export type GetGoogleAnalytics4MetricVariables = {
  args: MetricValuesArgs;
  propertyId: string;
};

export type GetGoogleSearchConsoleMetricValuesVariables = {
  args: MetricValuesArgs;
};

export type SetConversationMessageReactionArgs = {
  messageId: string;
  helpful: boolean;
};

export type ShopifyMetricValues = {
  filtersOptions: { key: string; values: string[] }[];
  metricRows: MetricValuesRow[];
};
export type MetricValue = {
  value: string;
  key: string;
};
export type MetricValuesRow = {
  metricValues: MetricValue[];
  splitValues: (MetricValue | null)[];
};

export type MetricValuesRowsGroupedByConnector = {
  [key in ConnectorType]?: MetricValuesRow[];
} & {
  customFormula?: MetricValuesRow[];
};

export type MetricValuesArgsGroupedByConnector = {
  dateRange: DateRangeInput;
  splits?: string[];
  shopify?: {
    metrics: string[];
    splits?: string[];
    filters?: GA4FilterItem[];
  };
  facebook?: {
    metrics: string[];
    splits?: string[];
    filters?: GA4FilterItem[];
  };
  googleAds?: {
    metrics: string[];
    splits?: string[];
    filters?: GA4FilterItem[];
  };
  googleAnalytics4?: {
    metrics: string[];
    splits?: string[];
    filters?: GA4FilterItem[];
  };
  googleSearchConsole?: {
    metrics: string[];
    splits?: string[];
    filters?: GA4FilterItem[];
  };
};

export type GetShopifyMetricValuesVariables = {
  args: MetricValuesArgs;
};

export type GetKipifyMetricValuesVariables = {
  args: MetricValuesArgs;
};

export type CheckConnectorTokenValidityVariables = {
  connectorId: string;
};

export type MetricFilterItem = {
  key: string;
  operator: string;
  values: string[];
};

export type DateRangeInput = {
  from: string;
  to: string;
};

interface RuleAnd {
  operator: string;
  values: string[];
}

export interface RoleOr {
  rulesAnd: RuleAnd[];
}

export interface GA4FilterItem {
  key: string;
  filterGroupOr: RoleOr[];
}

export type MetricValuesArgs = {
  metricKeys: string[];
  splitsKeys: string[];
  filters: GA4FilterItem[];
  dateRange: DateRangeInput;
};

export type CustomFormulaArgs = BaseArgs & { formulaIds: string[] };
export type BaseArgs = {
  splitsKeys: string[];
  filters: MetricFilterItemInput[][];
  dateRange: DateRangeInput;
};

export type GetCustomFormulaArgs = {
  args: CustomFormulaArgs;
};

export type MetricFilterItemInput = {
  key: string;
  operator: string;
  values: string[];
};

export type GetFilterAvailableValuesVariables = {
  connectorType: ConnectorType;
  filterKey: string;
};

export type UpdateConversationContextVariables = {
  args: MutationUpdateConversationContextArgs;
};

export type Metric = {
  key: string;
  label: string;
  category: string;
  id: string;
  type: ConnectorType | MetricsByConnectorType;
  valueType: ValueType;
  incompatibleMetrics?: string[];
};

export type MetricGroup = {
  key: string;
  label: string;
  metrics: Metric[];
};

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  databaseConnectorEnabled?: boolean;
  shopifyConnectorEnabled?: boolean;
  magentoConnectorEnabled?: boolean;
  deckStudioEnabled?: boolean;
  company: Company;
};

export type Company = {
  id: string;
  name: string;
  industry: string;
  companyDescription: string;
  usageOptions: UsageOption[];
};

export type Connector = {
  id: string;
  connectorType: ConnectorType;
  displayName?: string;
  properties?: {
    selected: boolean;
    id: string;
  }[];
};

export type ConnectorSelection = {
  connectorId: string;
  connectorType: ConnectorType;
  selectedAccountIds: string[];
  selectedSubaccountIds: string[];
};

export type Filter = {
  type: ConnectorType;
  operators: MetricFilterOperator[];
  label: string;
  key: string;
  id: string;
  expectedReturnValueType: FilterValueType;
  availableValues?: MetricFilterAvailableValue[];
  shouldAvailableValuesBeRequested: boolean;
};

export type MetricFilterOperator = {
  label: string;
  key: string;
};

export type MetricFilterAvailableValue = {
  label: string;
  key: string;
};

type AnalyticsProperty = {
  id: string;
  name: string;
  selected: boolean;
};

type GoogleAdsSubaccount = AnalyticsProperty;

export type AnalyticsAccount = {
  id: string;
  name: string;
  selected: boolean;
  properties: AnalyticsProperty[];
};

export type GoogleAdsAccount = {
  id: string;
  selected: boolean;
  subaccounts: GoogleAdsSubaccount[];
};

export type FacebookAdAccount = {
  id: string;
  name: string;
  selected: boolean;
};

export type SearchConsoleSite = {
  id: string;
  name: string;
  selected: boolean;
};

export type DatabaseConnection = {
  id: string;
  name: string;
  selected: boolean;
};

export type AccountSelectionInput = {
  accountId: string;
  subaccountIds: string[];
};

export type Split = {
  id: string;
  label: string;
  key: string;
  connectorType: ConnectorType;
  valueType?: ValueType;
};

export type Conversation = {
  id: string;
  createdAt: string;
  name: string;
  messages: ConversationMessage[];
};

export type ConversationMessageTable = {
  dateRange: DateRangeInput;
  rows: MetricValuesRowsGroupedByConnector;
  args: MetricValuesArgsGroupedByConnector;
};

export type ConversationMessageAttachment = {
  kind: string;
  filename: string;
  mimeType: string;
  content: string;
};

export type ConversationMessage = {
  id: string;
  createdAt: string;
  role: ConversationMessageRole;
  text: string;
  table?: ConversationMessageTable;
  helpful?: boolean;
  attachments?: ConversationMessageAttachment[] | null;
};

export enum ConversationMessageRole {
  Assistant = 'Assistant',
  User = 'User',
}

export enum CustomFormulaItemType {
  operator = 'operator',
  metric = 'metric',
  brackets = 'brackets',
  constant = 'constant',
}

export enum UsageOption {
  ImproveSales = 'ImproveSales',
  ImproveCompanyMargin = 'ImproveCompanyMargin',
  ReduceCostOfAdvertising = 'ReduceCostOfAdvertising',
  Unknown = 'Unknown',
}

export enum ConnectorType {
  SHOPIFY = 'shopify',
  FACEBOOK = 'facebook',
  GOOGLE_ADS = 'googleAds',
  GOOGlE_ANALYTICS4 = 'googleAnalytics4',
  GOOGLE_SEARCH_CONSOLE = 'googleSearchConsole',
  KIPIFY = 'kipify',
  DATABASE = 'database',
  MAGENTO = 'magento',
}
export enum MetricsByConnectorType {
  KIPIFY = 'kipify',
  CUSTOM_FORMULA = 'customFormula',
}

export enum ConnectorLabel {
  SHOPIFY = 'Shopify',
  FACEBOOK = 'Facebook Ads',
  GOOGLE_ADS = 'Google Ads',
  GOOGlE_ANALYTICS4 = 'Google Analytics 4',
  GOOGLE_SEARCH_CONSOLE = 'Google Search Console',
  KIPIFY = 'Kipify',
  CUSTOM_FORMULA = 'Custom Formula',
  MAGENTO = 'Magento',
}

export enum MetricFormatType {
  Number = 'number',
  Percent = 'percent',
  Currency = 'currency',
  Duration = 'duration',
}

export enum FilterValueType {
  ArrayInt = 'ArrayInt',
  ArrayStr = 'ArrayStr',
}
export type DataSourceConfig = {
  data?: MetricValuesRow[];
  type: ConnectorType | MetricsByConnectorType;
  // Elenco delle metriche disponibili per il data source (per valueType delle celle)
  metrics?: Metric[];
};

export enum ValueType {
  string = 'string',
  date = 'date',
  number = 'number',
  currency = 'currency',
  image = 'image',
  percentage = 'percentage',
  duration = 'duration',
}

export type CustomReportInput = {
  name: string;
  config: any;
};

export type CustomReport = {
  id: string;
  name: string;
  config: any;
  createdAt: string;
  updatedAt: string;
};

export type MutationCreateCustomReportArgs = {
  input: CustomReportInput;
};

export type KpiSectionInput = {
  name: string;
  config: any;
};

export type KpiSection = {
  id: string;
  name: string;
  config: any;
  createdAt: string;
  updatedAt: string;
};

export type MutationCreateKpiSectionArgs = {
  input: KpiSectionInput;
};

export type ChangePasswordInput = {
  currentPassword: string;
  newPassword: string;
};

export type InviteStatus = 'pending' | 'accepted' | 'expired' | 'cancelled';

export type CompanyInvite = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: InviteStatus;
  createdAt: string;
  expiresAt: string;
  company?: Company;
  invitedBy?: User;
  acceptedBy?: User;
};

export type InviteUserInput = {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
};

export type AcceptInviteInput = {
  inviteToken: string;
  password: string;
};

export type UserMetricPermission = {
  id: string;
  metricKey: string;
  connectorType: ConnectorType;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  user?: User;
  grantedBy?: User;
};

export type UpdateMetricPermissionInput = {
  userId: string;
  metricKey: string;
  connectorType: ConnectorType;
  isEnabled: boolean;
};

export type UpdateMultipleMetricPermissionsInput = {
  userId: string;
  connectorType: ConnectorType;
  enabledMetrics: string[];
};

export type UnifiedSearchTerm = {
  __typename?: 'UnifiedSearchTerm';
  date?: string | null;
  campaignId: string;
  campaignName: string;
  campaignType: string;
  adGroupId?: string | null;
  adGroupName?: string | null;
  searchTerm: string;
  impressions?: number | null;
  clicks?: number | null;
  costMicros?: number | null;
  sourceView: string;
};
